<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue'

const colorMode = useColorMode()
const surface = ref<{ width: number, height: number, background: string, backgroundSize: string } | null>(null)
const surfaceStyle = computed(() => surface.value && ({
  width: `${surface.value.width}px`,
  height: `${surface.value.height}px`,
  background: surface.value.background,
  backgroundSize: surface.value.backgroundSize,
  backgroundPosition: '0 0',
}))
let pendingFrame = 0
let dispose: (() => void) | undefined

onMounted(() => {
  const orientation = screen.orientation
  const viewport = window.visualViewport
  if (!orientation || !viewport) return
  const root = document.documentElement
  const content = document.querySelector<HTMLElement>('.app-container')
  const dots = document.querySelector<HTMLElement>('.global-dotted-bg')
  if (!content || !dots) throw new Error('Duo sidebar backdrop requires the app background')

  function clear() {
    if (!surface.value) return
    surface.value = null
    root.classList.remove('duo-sidebar-active', 'duo-sidebar-unzoomed')
    root.style.removeProperty('--duo-content-offset')
    window.scrollTo({ left: 0, top: window.scrollY, behavior: 'instant' })
  }

  const update = async () => {
    pendingFrame = 0
    const angle = orientation.angle
    // No device-name API exists. Fail closed outside the observed Duo outer display.
    const isDuoOuterDisplay = screen.width === 466 && screen.height === 678
      && window.devicePixelRatio === 3 && navigator.maxTouchPoints > 0
      && matchMedia('(pointer: coarse)').matches
      && CSS.supports('-webkit-touch-callout', 'none')
    const width = root.clientWidth
    const screenWidth = angle === 90 || angle === 270 ? screen.height : screen.width
    const gap = screenWidth - width
    if (colorMode.value !== 'dark' || !isDuoOuterDisplay
      || ![0, 90, 270].includes(angle) || Math.abs(gap - 84) > 1) {
      clear()
      return
    }

    const offset = angle === 90 ? gap : 0
    const pattern = getComputedStyle(dots)
    surface.value = {
      width: width + gap,
      height: content.getBoundingClientRect().height,
      background: pattern.background,
      backgroundSize: pattern.backgroundSize,
    }
    root.style.setProperty('--duo-content-offset', `${offset}px`)
    root.classList.add('duo-sidebar-active')
    const unzoomed = viewport.scale <= 1.01
    root.classList.toggle('duo-sidebar-unzoomed', unzoomed)
    await nextTick()
    // Left-side overscan must occupy positive document space. Negative overflow is clipped.
    if (unzoomed && surface.value && window.scrollX !== offset) {
      window.scrollTo({ left: offset, top: window.scrollY, behavior: 'instant' })
    }
  }

  function schedule() {
    if (!pendingFrame) pendingFrame = requestAnimationFrame(update)
  }

  const observer = new ResizeObserver(schedule)
  observer.observe(content)
  window.addEventListener('resize', schedule)
  window.addEventListener('scroll', schedule, { passive: true })
  orientation.addEventListener('change', schedule)
  viewport.addEventListener('resize', schedule)
  viewport.addEventListener('scroll', schedule)
  const stopThemeWatch = watch(() => colorMode.value, schedule)
  schedule()

  dispose = () => {
    cancelAnimationFrame(pendingFrame)
    observer.disconnect()
    stopThemeWatch()
    window.removeEventListener('resize', schedule)
    window.removeEventListener('scroll', schedule)
    orientation.removeEventListener('change', schedule)
    viewport.removeEventListener('resize', schedule)
    viewport.removeEventListener('scroll', schedule)
    clear()
  }
})

onBeforeUnmount(() => dispose?.())
</script>

<template>
  <Teleport to="body">
    <div v-if="surface" class="duo-sidebar-backdrop" :style="surfaceStyle" aria-hidden="true" />
  </Teleport>
</template>

<style>
html.duo-sidebar-active body {
  position: relative;
  isolation: isolate;
}

html.duo-sidebar-active #__nuxt {
  position: relative;
  z-index: 1;
  width: 100%;
  margin-left: var(--duo-content-offset);
}

html.duo-sidebar-active .global-dotted-bg {
  display: none;
}

/* One document-anchored surface keeps both sides in phase during native scrolling and zoom. */
.duo-sidebar-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  pointer-events: none;
}

html.duo-sidebar-unzoomed,
html.duo-sidebar-unzoomed body {
  touch-action: pan-y pinch-zoom;
}
</style>
